import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGameData } from "./GameContext";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { gameDetails } from "../App";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const USERS_PER_PAGE = 10;

interface EchoesOfSelfSession {
    userId: string;
    sessionTime: number;
    maxLevel: number;
    confirmClicked: boolean;
    purchaseClicked: boolean;
    registrationDate: string;
    userIdTest: string;
    CanvasQuestion?: string;
    FoodItemController?: string;
    GhostMan?: string;
    ImageQuestion?: string;
    Inventory?: string;
    LampMan?: string;
    MemoryManager?: string;
    PlayerPositions?: string;
    QuiltMan?: string;
    DiceGame?: string;
    ColorClickData?: string;
    Violin?: string;
    InventoryMBKIComparator?: string;
}

interface Position {
    x: number;
    y: number;
    z: number;
}

interface PlayerPositionsData {
    playerPositions: Position[];
    metadata: {
        recordCount: number;
        recordDate: string;
        gameVersion: string;
        levelName: string;
    };
}

const PlayerPathCanvas: React.FC<{ positions: Position[] }> = ({ positions }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
    const backgroundImage = new Image();
    backgroundImage.src = '/game-map.jpg';

    useEffect(() => {
        backgroundImage.onload = () => {
            setCanvasSize({
                width: backgroundImage.width,
                height: backgroundImage.height
            });
        };
    }, []);

    useEffect(() => {
        console.log('Drawing positions:', positions);
        const canvas = canvasRef.current;
        if (!canvas || positions.length === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const drawCanvas = () => {
            // Clear canvas
            ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);

            // Draw background image
            ctx.drawImage(backgroundImage, 0, 0, canvasSize.width, canvasSize.height);

            // Set up canvas with more visible colors
            ctx.strokeStyle = 'rgba(37, 99, 235, 0.8)';
            ctx.lineWidth = 2;

            // Find min and max values for scaling
            const xValues = positions.map(p => p.x);
            const yValues = positions.map(p => -p.y); // Invert Y coordinates
            const minX = Math.min(...xValues);
            const maxX = Math.max(...xValues);
            const minY = Math.min(...yValues);
            const maxY = Math.max(...yValues);

            // Calculate ranges
            const rangeX = maxX - minX;
            const rangeY = maxY - minY;

            // Calculate scale factors with adjusted padding
            const padding = 0.15; // 15% padding
            const paddedRangeX = rangeX * (1 + padding * 7.2);
            const paddedRangeY = rangeY * (1 + padding * 7.2);

            // Calculate scale while maintaining aspect ratio
            const scaleX = canvasSize.width / paddedRangeX;
            const scaleY = canvasSize.height / paddedRangeY;
            const scale = Math.min(scaleX, scaleY);

            // Calculate offsets to center the path with additional vertical offset
            const offsetX = ((canvasSize.width - rangeX * scale) / 2)+100;
            const offsetY = ((canvasSize.height - rangeY * scale) / 2)-60; // Added 20 units down

            // Draw path with glow effect
            ctx.shadowColor = 'rgba(37, 99, 235, 0.5)';
            ctx.shadowBlur = 3;
            ctx.beginPath();

            positions.forEach((pos, index) => {
                // Normalize and scale coordinates
                const x = (pos.x - minX) * scale + offsetX;
                const y = (-pos.y - minY) * scale + offsetY; // Using updated offsetY

                if (index === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            });
            ctx.stroke();

            // Draw points
            if (positions.length > 0) {
                // Start point (green)
                const startX = (positions[0].x - minX) * scale + offsetX;
                const startY = (-positions[0].y - minY) * scale + offsetY;
                ctx.fillStyle = 'rgba(5, 150, 105, 0.9)';
                ctx.shadowColor = 'rgba(5, 150, 105, 0.7)';
                ctx.beginPath();
                ctx.arc(startX, startY, 6, 0, Math.PI * 2);
                ctx.fill();

                // End point (red)
                const endX = (positions[positions.length - 1].x - minX) * scale + offsetX;
                const endY = (-positions[positions.length - 1].y - minY) * scale + offsetY;
                ctx.fillStyle = 'rgba(220, 38, 38, 0.9)';
                ctx.shadowColor = 'rgba(220, 38, 38, 0.7)';
                ctx.beginPath();
                ctx.arc(endX, endY, 6, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        // Draw initially and when background image loads
        drawCanvas();
        backgroundImage.onload = drawCanvas;
    }, [positions, canvasSize]);

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">Oyuncu Hareket Yolu</h4>
            <div className="relative overflow-auto">
                <canvas
                    ref={canvasRef}
                    width={canvasSize.width}
                    height={canvasSize.height}
                    className="border-2 border-gray-300 rounded-lg bg-white shadow-md"
                    style={{ width: '100%', height: 'auto', maxHeight: '600px' }}
                />
                <div className="absolute bottom-2 right-2 flex items-center space-x-4 text-xs text-gray-600 bg-white/80 px-2 py-1 rounded">
                    <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-600 mr-1"></div>
                        <span>Başlangıç</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-red-600 mr-1"></div>
                        <span>Bitiş</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const UsersPageEchoesOfSelf: React.FC = () => {
    const navigate = useNavigate();
    const { gameData } = useGameData();
    const { t } = useLanguage();
    const sessions = gameData["game7"] || [];
    const [currentPage, setCurrentPage] = useState(1);
    const [userIdFilter, setUserIdFilter] = useState<string>("");
    const [purchaseFilter, setPurchaseFilter] = useState<boolean | null>(null);
    const [confirmFilter, setConfirmFilter] = useState<boolean | null>(null);

    const handleBack = () => {
        navigate("/");
    };

    const formatSessionTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        let result = "";
        if (hours > 0) result += `${hours}${t("h")} `;
        if (minutes > 0) result += `${minutes}${t("m")} `;
        if (remainingSeconds > 0 || result === "") result += `${remainingSeconds}${t("s")}`;
        return result.trim();
    };

    const filteredSessions = sessions.filter((session) => {
        return (
            (!userIdFilter || session.userId.toLowerCase().includes(userIdFilter.toLowerCase())) &&
            (purchaseFilter === null || session.purchaseClicked === purchaseFilter) &&
            (confirmFilter === null || session.confirmClicked === confirmFilter)
        );
    });

    const totalPages = Math.ceil(filteredSessions.length / USERS_PER_PAGE);
    const startIndex = (currentPage - 1) * USERS_PER_PAGE;
    const endIndex = startIndex + USERS_PER_PAGE;
    const paginatedSessions = filteredSessions.slice(startIndex, endIndex);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const parseJSON = (jsonString: string | undefined) => {
        try {
            return jsonString ? JSON.parse(jsonString) : null;
        } catch (error) {
            console.error("JSON parse error:", error);
            return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <main className="mt-36 mb-16 flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-4">
                        <img
                            src={gameDetails["game7"]?.logo || ""}
                            alt="Echoes of Self"
                            className="w-12 h-12 rounded-full"
                        />
                        <h2 className="text-3xl font-bold text-gray-800">Echoes of Self {t("users")}</h2>
                    </div>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                    >
                        {t("back")}
                    </button>
                </motion.div>

                {/* Filtreleme Bölümü */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">{t("data_filtering")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("user_id")}</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={userIdFilter}
                                    onChange={(e) => setUserIdFilter(e.target.value)}
                                    placeholder={t("eg_user_id")}
                                    className="w-full border border-gray-300 rounded-lg p-2 pl-9 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-2 top-2.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("purchase_status")}</label>
                            <select
                                value={purchaseFilter === null ? "" : purchaseFilter ? "true" : "false"}
                                onChange={(e) => setPurchaseFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white pl-4 pr-10"
                                style={{ backgroundImage: "url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"%3e%3cpolyline points=\"6 9 12 15 18 9\"%3e%3c/polyline%3e%3c/svg%3e')", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("purchased")}</option>
                                <option value="false">{t("not_purchased")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("confirm_status")}</label>
                            <select
                                value={confirmFilter === null ? "" : confirmFilter ? "true" : "false"}
                                onChange={(e) => setConfirmFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white pl-4 pr-10"
                                style={{ backgroundImage: "url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"%3e%3cpolyline points=\"6 9 12 15 18 9\"%3e%3c/polyline%3e%3c/svg%3e')", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("confirmed")}</option>
                                <option value="false">{t("not_confirmed")}</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button 
                            onClick={() => {
                                setUserIdFilter("");
                                setPurchaseFilter(null);
                                setConfirmFilter(null);
                            }}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors mr-2"
                        >
                            {t("clear_filters")}
                        </button>
                    </div>
                </div>

                {/* Kullanıcı Kartları */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedSessions.map((session) => {
                        const canvasQuestion = parseJSON(session.CanvasQuestion);
                        const ghostMan = parseJSON(session.GhostMan);
                        const lampMan = parseJSON(session.LampMan);
                        const quiltMan = parseJSON(session.QuiltMan);
                        const playerPositions = parseJSON(session.PlayerPositions);
                        const colorClickData = parseJSON(session.ColorClickData);
                        const violinData = parseJSON(session.Violin);

                        return (
                            <motion.div
                                key={session.userId}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl shadow-lg p-6"
                            >
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                    {" "}
                                    <span className="relative group">
                                        <span className="text-gray-800">#</span>
                                        <span className="absolute left-full ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 px-2 py-1 rounded text-sm text-gray-700">
                                            {session.userId}
                                        </span>
                                    </span>
                                </h3>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="p-3 bg-gray-100 rounded-lg">
                                            <span className="text-xs text-gray-500">{t("session_time")}</span>
                                            <p className="text-sm font-medium text-gray-800 mt-1">{formatSessionTime(session.sessionTime)}</p>
                                        </div>
                                        <div className="p-3 bg-gray-100 rounded-lg">
                                            <span className="text-xs text-gray-500">{t("max_level")}</span>
                                            <p className="text-sm font-medium text-gray-800 mt-1">{session.maxLevel}</p>
                                        </div>
                                        <div className="p-3 bg-gray-100 rounded-lg">
                                            <span className="text-xs text-gray-500">{t("registration_date")}</span>
                                            <p className="text-sm font-medium text-gray-800 mt-1">{new Date(session.registrationDate).toLocaleString()}</p>
                                        </div>
                                        <div className="p-3 bg-gray-100 rounded-lg flex items-center space-x-2">
                                            <div className="flex-1">
                                                <span className="text-xs text-gray-500">Durum</span>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${session.purchaseClicked ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {session.purchaseClicked ? t("purchased") : t("not_purchased")}
                                                    </span>
                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${session.confirmClicked ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {session.confirmClicked ? t("confirmed") : t("not_confirmed")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Canvas Question */}
                                    {canvasQuestion && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">Canvas Soruları</h4>
                                            {(() => {
                                                try {
                                                    const parsedData = typeof canvasQuestion === 'string' ? JSON.parse(canvasQuestion) : canvasQuestion;
                                                    if (parsedData?.sessions?.[0]?.answers) {
                                                        return (
                                                            <div className="space-y-3">
                                                                <div className="bg-blue-50 p-3 rounded-lg">
                                                                    <p className="text-sm font-medium text-blue-700">Toplam Soru</p>
                                                                    <p className="text-lg font-bold text-blue-800">{parsedData.sessions[0].answers.length}</p>
                                                                </div>
                                                                <div className="mt-2">
                                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Soru Detayları</h5>
                                                                    <div className="space-y-2">
                                                                        {parsedData.sessions[0].answers.map((answer: any, index: number) => (
                                                                            <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-blue-400">
                                                                                <p className="text-sm font-medium text-gray-700">{answer.questionText}</p>
                                                                                <p className="text-sm text-blue-600 mt-1">{answer.selectedAnswerText}</p>
                                                                                <p className="text-xs text-gray-500 mt-1">
                                                                                    Yanıt Süresi: {answer.responseTimeSeconds.toFixed(2)} saniye
                                                                                </p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return <p className="text-sm text-red-600">Geçersiz soru verisi</p>;
                                                } catch (error) {
                                                    console.error("Error parsing canvas questions:", error);
                                                    return <p className="text-sm text-red-600">Soru verisi işlenirken hata oluştu</p>;
                                                }
                                            })()}
                                        </div>
                                    )}

                                    {/* Ghost Man */}
                                    {ghostMan?.sessions?.[0]?.answers && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">{t("ghost_man_questions")}</h4>
                                            <div className="space-y-3">
                                                <div className="bg-purple-50 p-3 rounded-lg">
                                                    <p className="text-sm font-medium text-purple-700">Toplam Soru</p>
                                                    <p className="text-lg font-bold text-purple-800">{ghostMan.sessions[0].answers.length}</p>
                                                </div>
                                                <div className="mt-2">
                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Soru Detayları</h5>
                                                    <div className="space-y-2">
                                                        {ghostMan.sessions[0].answers.map((answer: any, index: number) => (
                                                            <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-purple-400">
                                                                <p className="text-sm font-medium text-gray-700">{answer.questionText}</p>
                                                                <p className="text-sm text-purple-600 mt-1">{answer.selectedAnswerText}</p>
                                                                {answer.responseTimeSeconds && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        Yanıt Süresi: {answer.responseTimeSeconds.toFixed(2)} saniye
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Lamp Man */}
                                    {lampMan?.sessions?.[0]?.answers && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">{t("lamp_man_questions")}</h4>
                                            <div className="space-y-3">
                                                <div className="bg-yellow-50 p-3 rounded-lg">
                                                    <p className="text-sm font-medium text-yellow-700">Toplam Soru</p>
                                                    <p className="text-lg font-bold text-yellow-800">{lampMan.sessions[0].answers.length}</p>
                                                </div>
                                                <div className="mt-2">
                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Soru Detayları</h5>
                                                    <div className="space-y-2">
                                                        {lampMan.sessions[0].answers.map((answer: any, index: number) => (
                                                            <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-yellow-400">
                                                                <p className="text-sm font-medium text-gray-700">{answer.questionText}</p>
                                                                <p className="text-sm text-yellow-600 mt-1">{answer.selectedAnswerText}</p>
                                                                {answer.responseTimeSeconds && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        Yanıt Süresi: {answer.responseTimeSeconds.toFixed(2)} saniye
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Quilt Man */}
                                    {quiltMan?.sessions?.[0]?.answers && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">{t("quilt_man_questions")}</h4>
                                            <div className="space-y-3">
                                                <div className="bg-green-50 p-3 rounded-lg">
                                                    <p className="text-sm font-medium text-green-700">Toplam Soru</p>
                                                    <p className="text-lg font-bold text-green-800">{quiltMan.sessions[0].answers.length}</p>
                                                </div>
                                                <div className="mt-2">
                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Soru Detayları</h5>
                                                    <div className="space-y-2">
                                                        {quiltMan.sessions[0].answers.map((answer: any, index: number) => (
                                                            <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-green-400">
                                                                <p className="text-sm font-medium text-gray-700">{answer.questionText}</p>
                                                                <p className="text-sm text-green-600 mt-1">{answer.selectedAnswerText}</p>
                                                                {answer.responseTimeSeconds && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        Yanıt Süresi: {answer.responseTimeSeconds.toFixed(2)} saniye
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Dice Game */}
                                    {session.DiceGame && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">Zar Oyunu</h4>
                                            {(() => {
                                                try {
                                                    const diceGameData = parseJSON(session.DiceGame);
                                                    if (diceGameData?.sessions) {
                                                        return (
                                                            <div className="space-y-4">
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div className="bg-blue-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-blue-700">Toplam Oyun</p>
                                                                        <p className="text-lg font-bold text-blue-800">{diceGameData.totalGamesPlayed}</p>
                                                                    </div>
                                                                    <div className="bg-purple-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-purple-700">Toplam Süre</p>
                                                                        <p className="text-lg font-bold text-purple-800">{diceGameData.totalPlayTime.toFixed(2)} sn</p>
                                                                    </div>
                                                                    <div className="bg-green-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-green-700">Kazanılan Anılar</p>
                                                                        <p className="text-lg font-bold text-green-800">{diceGameData.totalMemoriesWon}</p>
                                                                    </div>
                                                                    <div className="bg-red-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-red-700">Kaybedilen Anılar</p>
                                                                        <p className="text-lg font-bold text-red-800">{diceGameData.totalMemoriesLost}</p>
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="mt-3">
                                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Oyun Detayları</h5>
                                                                    <div className="space-y-2">
                                                                        {diceGameData.sessions.map((session: any, index: number) => (
                                                                            <div key={index} className={`p-3 rounded-lg ${session.isWin ? 'bg-green-50 border-l-4 border-green-500' : 'bg-red-50 border-l-4 border-red-500'}`}>
                                                                                <div className="flex justify-between items-center">
                                                                                    <span className="text-sm font-medium">{index + 1}. Oyun</span>
                                                                                    <span className={`text-sm font-medium ${session.isWin ? 'text-green-700' : 'text-red-700'}`}>
                                                                                        {session.isWin ? 'Kazandı' : 'Kaybetti'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                                                                                    <div>
                                                                                        <span className="text-gray-600">Bahis: </span>
                                                                                        <span className="font-medium">{session.betAmount}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="text-gray-600">Kazanç: </span>
                                                                                        <span className="font-medium">{session.wonAmount}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="text-gray-600">Süre: </span>
                                                                                        <span className="font-medium">{session.sessionDuration.toFixed(1)} sn</span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return <p className="text-sm text-red-600">Geçersiz zar oyunu verisi</p>;
                                                } catch (error) {
                                                    console.error("Error parsing dice game data:", error);
                                                    return <p className="text-sm text-red-600">Zar oyunu verisi işlenirken hata oluştu</p>;
                                                }
                                            })()}
                                        </div>
                                    )}
                                    
                                    {/* Player Positions */}
                                    {playerPositions && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">Oyuncu Pozisyonları</h4>
                                            {(() => {
                                                try {
                                                    const parsedData = typeof playerPositions === 'string' ? JSON.parse(playerPositions) : playerPositions;
                                                    if (parsedData?.playerPositions) {
                                                        return (
                                                            <div className="space-y-3">
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div className="bg-indigo-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-indigo-700">Toplam Pozisyon</p>
                                                                        <p className="text-lg font-bold text-indigo-800">{parsedData.playerPositions.length}</p>
                                                                    </div>
                                                                    <div className="bg-teal-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-teal-700">Kayıt Tarihi</p>
                                                                        <p className="text-sm font-bold text-teal-800">{new Date(parsedData.metadata.recordDate).toLocaleString()}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-2">
                                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Hareket Haritası</h5>
                                                                    <PlayerPathCanvas positions={parsedData.playerPositions} />
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return <p className="text-sm text-red-600">Geçersiz pozisyon verisi</p>;
                                                } catch (error) {
                                                    console.error("Error parsing player positions:", error);
                                                    return <p className="text-sm text-red-600">Pozisyon verisi işlenirken hata oluştu</p>;
                                                }
                                            })()}
                                        </div>
                                    )}
                                    
                                    {/* Memory Manager */}
                                    {session.MemoryManager && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">Hafıza Yöneticisi</h4>
                                            {(() => {
                                                try {
                                                    const memoryData = parseJSON(session.MemoryManager);
                                                    if (memoryData) {
                                                        return (
                                                            <div className="space-y-4">
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div className="bg-amber-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-amber-700">Toplam Anılar</p>
                                                                        <p className="text-lg font-bold text-amber-800">{memoryData.TotalMemories}</p>
                                                                    </div>
                                                                    <div className="bg-cyan-50 p-3 rounded-lg">
                                                                        <p className="text-sm font-medium text-cyan-700">Önceki Toplam</p>
                                                                        <p className="text-lg font-bold text-cyan-800">{memoryData.PreviousTotalMemories}</p>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Scene Memories */}
                                                                {memoryData.SceneMemoriesData && memoryData.SceneMemoriesData.length > 0 && (
                                                                    <div className="mt-3">
                                                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Sahne Anıları</h5>
                                                                        <div className="space-y-2">
                                                                            {memoryData.SceneMemoriesData.map((scene: any, index: number) => (
                                                                                scene.Memories && scene.Memories.length > 0 && (
                                                                                    <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-amber-400">
                                                                                        <p className="text-sm font-medium text-gray-700">Sahne {scene.SceneIndex}</p>
                                                                                        <div className="mt-2 space-y-1">
                                                                                            {scene.Memories.map((memory: string, mIndex: number) => (
                                                                                                <p key={mIndex} className="text-sm text-amber-600 pl-2 border-l-2 border-amber-200">
                                                                                                    {memory}
                                                                                                </p>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Memory Transactions */}
                                                                {memoryData.MemoryTransactions && memoryData.MemoryTransactions.length > 0 && (
                                                                    <div className="mt-3">
                                                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Anı İşlemleri</h5>
                                                                        <div className="space-y-2">
                                                                            {memoryData.MemoryTransactions.map((transaction: any, index: number) => (
                                                                                <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-cyan-400">
                                                                                    <div className="flex justify-between items-center">
                                                                                        <span className="text-sm font-medium text-gray-700">{transaction.Description}</span>
                                                                                        <span className="text-xs text-gray-500">{transaction.Date}</span>
                                                                                    </div>
                                                                                    <div className="grid grid-cols-3 gap-2 mt-2">
                                                                                        <div className="text-xs">
                                                                                            <span className="text-gray-500">Önceki: </span>
                                                                                            <span className="font-medium">{transaction.PreviousBalance}</span>
                                                                                        </div>
                                                                                        <div className="text-xs">
                                                                                            <span className="text-gray-500">Eklenen: </span>
                                                                                            <span className="font-medium text-green-600">+{transaction.AmountAdded}</span>
                                                                                        </div>
                                                                                        <div className="text-xs">
                                                                                            <span className="text-gray-500">Yeni: </span>
                                                                                            <span className="font-medium">{transaction.NewBalance}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                                        Sahne: {transaction.SceneIndex}
                                                                                    </p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return <p className="text-sm text-red-600">Geçersiz hafıza yöneticisi verisi</p>;
                                                } catch (error) {
                                                    console.error("Error parsing memory manager data:", error);
                                                    return <p className="text-sm text-red-600">Hafıza yöneticisi verisi işlenirken hata oluştu</p>;
                                                }
                                            })()}
                                        </div>
                                    )}

                                    {/* Color Click Data */}
                                    {session.ColorClickData && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">Renk Tıklamaları</h4>
                                            {(() => {
                                                try {
                                                    const colorData = parseJSON(session.ColorClickData);
                                                    if (colorData?.clicks && colorData.clicks.length > 0) {
                                                        // Group clicks by color
                                                        const colorCounts: Record<string, number> = {};
                                                        colorData.clicks.forEach((click: any) => {
                                                            colorCounts[click.colorName] = (colorCounts[click.colorName] || 0) + click.clickCount;
                                                        });

                                                        // Helper function to get appropriate background color for each color name
                                                        const getColorClass = (colorName: string) => {
                                                            const colorMap: Record<string, string> = {
                                                                'Kırmızı': 'bg-red-100 text-red-800 border-red-300',
                                                                'Mavi': 'bg-blue-100 text-blue-800 border-blue-300',
                                                                'Yeşil': 'bg-green-100 text-green-800 border-green-300',
                                                                'Sarı': 'bg-yellow-100 text-yellow-800 border-yellow-300',
                                                                'Cyan': 'bg-cyan-100 text-cyan-800 border-cyan-300',
                                                                'Magenta': 'bg-pink-100 text-pink-800 border-pink-300',
                                                                'Turuncu': 'bg-orange-100 text-orange-800 border-orange-300',
                                                                'Mor': 'bg-purple-100 text-purple-800 border-purple-300',
                                                                'Koyu Yeşil': 'bg-emerald-100 text-emerald-800 border-emerald-300',
                                                                'Gri': 'bg-gray-100 text-gray-800 border-gray-300',
                                                            };
                                                            return colorMap[colorName] || 'bg-gray-100 text-gray-800 border-gray-300';
                                                        };

                                                        return (
                                                            <div className="space-y-4">
                                                                <div className="bg-violet-50 p-3 rounded-lg">
                                                                    <p className="text-sm font-medium text-violet-700">Toplam Tıklama</p>
                                                                    <p className="text-lg font-bold text-violet-800">{colorData.clicks.length}</p>
                                                                </div>
                                                                
                                                                {/* Color Statistics */}
                                                                <div className="mt-3">
                                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Renk Dağılımı</h5>
                                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                                        {Object.entries(colorCounts).map(([colorName, count], index) => (
                                                                            <div key={index} className={`p-2 rounded-lg border ${getColorClass(colorName)}`}>
                                                                                <p className="text-xs font-medium">{colorName}</p>
                                                                                <p className="text-lg font-bold">{count}</p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                {/* Click Timeline */}
                                                                <div className="mt-3">
                                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Tıklama Zaman Çizelgesi</h5>
                                                                    <div className="space-y-2">
                                                                        {colorData.clicks.map((click: any, index: number) => (
                                                                            <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-violet-300 flex justify-between items-center">
                                                                                <div className="flex items-center">
                                                                                    <div className={`w-4 h-4 rounded-full mr-2 ${getColorClass(click.colorName)}`}></div>
                                                                                    <span className="text-sm font-medium text-gray-700">{click.colorName}</span>
                                                                                </div>
                                                                                <span className="text-xs text-gray-500">{click.timestamp}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return <p className="text-sm text-red-600">Geçersiz renk tıklama verisi</p>;
                                                } catch (error) {
                                                    console.error("Error parsing color click data:", error);
                                                    return <p className="text-sm text-red-600">Renk tıklama verisi işlenirken hata oluştu</p>;
                                                }
                                            })()}
                                        </div>
                                    )}
                                    {console.log("Violin data:", session.Violin?.toString())}
                                    {/* Violin Data */}
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                        <h4 className="font-medium text-gray-700 mb-3">Keman Dinleme</h4>
                                        {(() => {
                                            console.log("Full session object:", JSON.stringify(session, null, 2));
                                            console.log("Session keys:", Object.keys(session));
                                            
                                            if (!session.Violin) {
                                                console.log("Violin property is missing in session");
                                                return <p className="text-sm text-gray-500">Keman dinleme verisi bulunamadı</p>;
                                            }

                                            try {
                                                const violinData = typeof session.Violin === 'string' ? JSON.parse(session.Violin) : session.Violin;
                                                console.log("Parsed Violin data:", violinData);

                                                if (!violinData || !violinData.sessions || !Array.isArray(violinData.sessions)) {
                                                    console.log("Invalid violin data structure:", violinData);
                                                    return <p className="text-sm text-red-600">Geçersiz keman dinleme verisi</p>;
                                                }

                                                return (
                                                    <div className="space-y-4">
                                                        <div className="bg-amber-50 p-3 rounded-lg">
                                                            <p className="text-sm font-medium text-amber-700">Toplam Dinleme Süresi</p>
                                                            <p className="text-lg font-bold text-amber-800">
                                                                {violinData.totalListeningDuration?.toFixed(2) || '0.00'} saniye
                                                            </p>
                                                        </div>
                                                        <div className="mt-3">
                                                            <h5 className="text-sm font-medium text-gray-700 mb-2">Dinleme Oturumları</h5>
                                                            <div className="space-y-2">
                                                                {violinData.sessions.map((session: any, index: number) => (
                                                                    <div key={index} className="p-3 bg-white shadow-sm rounded-lg border-l-4 border-amber-400">
                                                                        <div className="flex justify-between items-center">
                                                                            <div>
                                                                                <p className="text-sm font-medium text-gray-700">
                                                                                    {new Date(session.date).toLocaleString()}
                                                                                </p>
                                                                                <p className="text-xs text-amber-600 mt-1">
                                                                                    Süre: {session.duration?.toFixed(2) || '0.00'} saniye
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            } catch (error) {
                                                console.error("Error parsing violin data:", error);
                                                return <p className="text-sm text-red-600">Keman dinleme verisi işlenirken hata oluştu</p>;
                                            }
                                        })()}
                                    </div>

                                    {/* InventoryMBKIComparator */}
                                    {session.InventoryMBKIComparator && (
                                        <div className="mt-4 p-4 bg-gray-50 rounded-lg shadow border border-gray-200">
                                            <h4 className="font-medium text-gray-700 mb-3">Envanter ve MBKI Karşılaştırması</h4>
                                            {(() => {
                                                try {
                                                    const mbkiData = typeof session.InventoryMBKIComparator === 'string' 
                                                        ? JSON.parse(session.InventoryMBKIComparator) 
                                                        : session.InventoryMBKIComparator;

                                                    if (!mbkiData || !mbkiData.Results || !Array.isArray(mbkiData.Results)) {
                                                        return <p className="text-sm text-red-600">Geçersiz MBKI verisi</p>;
                                                    }

                                                    const result = mbkiData.Results[0];
                                                    return (
                                                        <div className="space-y-4">
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="bg-blue-50 p-3 rounded-lg">
                                                                    <p className="text-sm font-medium text-blue-700">Seçilen Eşyalar</p>
                                                                    <div className="mt-2 space-y-1">
                                                                        {result.InventoryItems.map((item: string, index: number) => (
                                                                            <p key={index} className="text-sm text-blue-800">{item}</p>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div className="bg-purple-50 p-3 rounded-lg">
                                                                    <p className="text-sm font-medium text-purple-700">MBKI Kombinasyonu</p>
                                                                    <div className="mt-2 space-y-1">
                                                                        {result.MBKICombination.map((item: string, index: number) => (
                                                                            <p key={index} className="text-sm text-purple-800">{item}</p>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="bg-green-50 p-3 rounded-lg">
                                                                <p className="text-sm font-medium text-green-700">MBTI Açıklaması</p>
                                                                <p className="text-sm text-green-800 mt-1">{result.MBTIDescription}</p>
                                                            </div>

                                                            <div className="bg-amber-50 p-3 rounded-lg">
                                                                <p className="text-sm font-medium text-amber-700">Çıkarımlar</p>
                                                                <p className="text-sm text-amber-800 mt-1">{result.Inference}</p>
                                                            </div>

                                                            <div className="bg-cyan-50 p-3 rounded-lg">
                                                                <p className="text-sm font-medium text-cyan-700">Yorum</p>
                                                                <p className="text-sm text-cyan-800 mt-1">{result.Comment}</p>
                                                            </div>

                                                            <div className="text-xs text-gray-500 mt-2">
                                                                Kayıt Tarihi: {new Date(mbkiData.Timestamp).toLocaleString()}
                                                            </div>
                                                        </div>
                                                    );
                                                } catch (error) {
                                                    console.error("Error parsing MBKI data:", error);
                                                    return <p className="text-sm text-red-600">MBKI verisi işlenirken hata oluştu</p>;
                                                }
                                            })()}
                                        </div>
                                    )}

                                    {/* Inventory */}
                                    {session.Inventory && (
                                        <div className="mt-4">
                                            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                                                <div className="p-6 border-b border-gray-100">
                                                    <h4 className="text-xl font-semibold text-gray-800 flex items-center">
                                                        <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                        </svg>
                                                        Envanter
                                                    </h4>
                                                </div>
                                                {(() => {
                                                    try {
                                                        const inventoryData = typeof session.Inventory === 'string' 
                                                            ? JSON.parse(session.Inventory) 
                                                            : session.Inventory;

                                                        if (!inventoryData || !inventoryData.items || !Array.isArray(inventoryData.items)) {
                                                            return (
                                                                <div className="p-6 text-center">
                                                                    <p className="text-sm text-red-600">Geçersiz envanter verisi</p>
                                                                </div>
                                                            );
                                                        }

                                                        const getItemImage = (instanceID: number) => {
                                                            const itemMap: { [key: number]: string } = {
                                                                37656: '/placeholder-items/wallclock.png', // Saat
                                                                37578: '/placeholder-items/pillow.png', // Yastık
                                                                37636: '/placeholder-items/key.png', // Anahtar
                                                                37590: '/placeholder-items/mask.png', // Maske
                                                                37622: '/placeholder-items/torch.png', // Fener
                                                            };
                                                            return itemMap[instanceID] || '/placeholder-items/UnknownItem.png';
                                                        };

                                                        const getItemName = (instanceID: number) => {
                                                            const nameMap: { [key: number]: string } = {
                                                                37656: 'Saat',
                                                                37578: 'Yastık',
                                                                37636: 'Anahtar',
                                                                37590: 'Maske',
                                                                37622: 'Fener',
                                                            };
                                                            return nameMap[instanceID] || t('unknown_item');
                                                        };

                                                        return (
                                                            <div className="p-6">
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                                                                    {inventoryData.items.map((item: any, index: number) => (
                                                                        <div 
                                                                            key={index} 
                                                                            className="group bg-gray-50 rounded-xl p-4 transition-all duration-300 hover:bg-white hover:shadow-md"
                                                                        >
                                                                            <div className="flex flex-col items-center">
                                                                                <div className="w-24 h-24 bg-white rounded-lg shadow-sm mb-3 p-3 flex items-center justify-center">
                                                                                    <img 
                                                                                        src={getItemImage(item.instanceID)} 
                                                                                        alt={getItemName(item.instanceID)}
                                                                                        className="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-300"
                                                                                        onError={(e) => {
                                                                                            const target = e.target as HTMLImageElement;
                                                                                            target.src = '/placeholder-items/glass.png';
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-center">
                                                                                    <h5 className="text-sm font-medium text-gray-800 mb-1">
                                                                                        {getItemName(item.instanceID)}
                                                                                    </h5>
                                                                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                                                        ID: {item.instanceID}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    } catch (error) {
                                                        console.error("Error parsing inventory data:", error);
                                                        return (
                                                            <div className="p-6 text-center">
                                                                <p className="text-sm text-red-600">Envanter verisi işlenirken hata oluştu</p>
                                                            </div>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                        <div className="flex space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
                            >
                                {t("previous")}
                            </button>
                            <span className="px-4 py-2">
                                {t("page")} {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
                            >
                                {t("next")}
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default UsersPageEchoesOfSelf; 